import {NoteEventTime,} from '@playground-sessions/pitch-detection';
import {playbackBuffer, prepareAudioBufferForTranscription, transcribeAudioBufferToMidiNotes,} from './soundUtilities';
import IAudioRecorder from "./IRecorder";
import MediaRecorderRecorder from "./mediaRecorderRecorder";
import AudioWorkletRecorder from "./audioWorkletRecorder";
import {getAudioContext} from "../audioContext";
import {displayErrorToUser} from "../error/error";
import * as Sentry from "@sentry/react";
import {logger} from "../log/logger";
import {microphone} from "../../hooks/microphone.hook";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {localStorageService} from "../storage/local-storage";
import storageKeys from "../storage/storageKeys";

// Consider moving this behavior into a component PitchDetector so the useMicrophone hook can be used there.
export default class PitchDetector {
  #audioContext: AudioContext;
  #mediaStream: MediaStream | null = null;
  #recordMode: "worklets" | "mediarecorder" = "mediarecorder";
  #onNotesDetected: (notes: NoteEventTime[]) => void;
  #onTranscribeUpdate: (percentComplete: number) => void;
  #audioRecorder: IAudioRecorder;
  #state: "listening" | "notListening" = "notListening";
  #audioContextIsSetup = false;
  #audioRecorderIsInitialized = false;

  constructor(
    onNotesDetected: (notes: NoteEventTime[]) => void,
    onTranscribeUpdate: (percentComplete: number) => void,
  ) {
    this.#onNotesDetected = onNotesDetected;
    this.#onTranscribeUpdate = onTranscribeUpdate;
    this.#setupAudioContextAndAudioRecorderIfPitchDetectionEnabled();
  }

  #setupAudioContextAndAudioRecorderIfPitchDetectionEnabled(): void {
    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    if (isPitchDetectionEnabled) {
      if (!this.#audioContextIsSetup) {
        this.#setupAudioContext();
      }
      if (!this.#audioRecorderIsInitialized) {
        this.#initializeAudioRecorder();
      }
    }
  }

  onPitchDetectionEnabledChanged() {
    this.#setupAudioContextAndAudioRecorderIfPitchDetectionEnabled();
  }

  /* This method initializes the audio recorder to use. There is more than one way to record audio in the browser.
    * One way to record Audio is to use a MediaRecorder object, which is a built-in browser object that records audio from the microphone.
    * The MediaRecorder object has some benefits
      * implementation is fairly straight forward
      * Seems to do everything we need for pitch detection
    * Another way to record audio is to use an AudioWorklet object, which is a custom object that records audio from the microphone.
    * The AudioWorklet has a variety of benefits
      * it is more flexible and can be used to record audio from other sources, such as a file or a stream.
      * it is more efficient and can be used to record audio in real-time.
      * it is more powerful and can be used to record audio in high-quality.
      * it is more reliable and can be used to record audio in a variety of formats.
      * it is more secure and can be used to record audio in a secure environment.
      * it is more scalable and can be used to record audio in a scalable environment.
    * DEV NOTES (DH - 6/3/2024):
      * I couldn't get the AudioWorklet approach to work - sound recorded doesn't sound right. It's slowed down and a bit distorted.
      * The AudioWorklet approach hasn't been fully coded yet. But since we're still in the prototyping stage and may end up
      * needing to use the AudioWorklet approach in the end, I'm going to leave it in the code for now.
   */
  async #initializeAudioRecorder(): Promise<void> {
    logger.pitchDetection.info({ message: "initializeAudioRecorder - Initializing audio recorder" });

    if (this.#recordMode === "mediarecorder") {
      this.#audioRecorder = new MediaRecorderRecorder();
    } else {
      this.#audioRecorder = new AudioWorkletRecorder();
    }

    return this.#audioRecorder.initialize(this.#audioContext)
      .then(() => {
        logger.pitchDetection.info({ message: "initializeAudioRecorder - Audio recorder initialized" });
      });
  }

  #setupAudioContext(): void {
    this.#audioContext = getAudioContext();
  }

  startListening(): void {
    const isListening = this.#state === "listening";
    if (isListening) {
      return;
    }
    this.#state = "listening";

    logger.pitchDetection.info({
      message: "pitchDetector: start message received - getting microphone access",
      data: [
        { key: "audioContext currentTime", value: getAudioContext().currentTime }
      ]
    });

    void PitchDetector.getMicrophoneAccess()
      .then((mediaStream) => {
        // If the user is presented with a native dialog to grant microphone access for the first time,
        // this code does not run.
        // Instead, the page reloads.
        this.#audioRecorder.startListening(mediaStream);
      })
      .catch((e) => {
        this.#state = "notListening";
        Sentry.captureException(e);
        logger.pitchDetection.error(e);
      });
  }

  static async getMicrophoneAccess(): Promise<MediaStream> {
    // BasicPitch requires a sample rate of 22050 Hz for pitch detection. No need to record sound at a higher sample rate.
    const sampleRate = 22050;

    // This component is responsible for using the microphone constraints that are appropriate for pitch detection.
    return microphone.getAccess({
      audio: {
        echoCancellation: false,
        noiseSuppression: true,
        autoGainControl: false,
        sampleRate: sampleRate,
      },
      video: false
    }).catch((e) => {
      if (e instanceof DOMException) {
        switch (e.name) {
          case "NotFoundError":
            displayErrorToUser("No microphone was found. It might be disabled or disconnected.");
            break;
          case "NotAllowedError":
            /**
             * In general, this error occurs under one of two conditions.
             * - Microphone access was denied by the user or the operating system.
             * - Or the native app lacks the ability to grant microphone permissions.
             * However, if this is called, we've already confirmed that we can grant microphone permissions,
             *   eliminating it as a possibility.
             * Therefore, at this point, microphone access was actively denied by the end user.
             * Display an appropriate error message
             */
            displayErrorToUser(`Microphone access is needed for pitch detection.`);
            break;
          case "OverconstrainedError":
            if (e instanceof OverconstrainedError) {
              displayErrorToUser(`No microphones found which satisfied the constraint "${e.constraint}" required for pitch detection: ${e.message}`);
            } else {
              displayErrorToUser("No microphones met the Playground-specific constraints required for pitch detection.");
            }
            Sentry.captureException(e); // We don't expect this error to occur.
            break;
          case "AbortError": // Fall through - A problem occurred which prevented the microphone from being used, even though the user and the OS both granted access, and no hardware errors occurred.
          case "InvalidStateError": // Fall through - Microphone permissions were improperly requested before the app was ready.
          case "NotReadableError": // Fall through -  Microphone could not be used due to a hardware error.
          case "SecurityError": // Fall through - User media was disabled on the Document on which getUserMedia was called for some reason.
          default: // New error types may be added in the future.
            Sentry.captureException(e); // We haven't seen these types of errors before.
            displayErrorToUser("Microphone permissions are required to use pitch detection.");
            break;
        }
      } else {
        Sentry.captureException(e); // We don't know what the error is.
        displayErrorToUser(`${e.message} - Microphone access is needed for pitch detection.`);
      }
      throw e;

      // This is the ideal permissions flow. We don't do this right now.
      // It's ideal because checking the permissions in the web app takes less time than checking native permissions.
      // so checking them first is the quickest experience when a keyboard is disconnected.

      // Currently, we don't follow this flow. We currently check native permissions first, then JS permissions.

      // (1) REQUEST microphone access from the WebView (determine if
      // If granted, start listening
      // Otherwise check the current status of the permission

      // (2) CHECK microphone permissions natively
      // "granted" | "denied" | "blocked" | "unavailable"
      // If granted
      // - Start listening (this should never happen, b/c we already checked in step 1)
      // If they are unavailable (e.g. doesn't have microphone)
      // - Show device-specific error message (could be any of the 4 platforms)
      // If blocked (i.e. not requestable on Apple, b/c previously denied or ignored)
      // - Show Apple-specific error message (iOS/MacOS)
      // If denied
      // - We are on Android, and the user has denied permissions in step 1.
      // - Show Android-specific error message
      // If no response w/in 500ms
      // - User is on an unsupported version of our app b/c can't request mic perms
      // - Start playback
    });
  }

  #getAudioContext(): AudioContext {
    return this.#audioContext;
  }

  #handleNotesDetected(notes: NoteEventTime[]) {
    // const notesSubset = this.#filterNotesByRangeAndRemoveDuplicates(notes);
    this.#onNotesDetected(notes);
  }

  async stopListening(analyzeAudio: boolean = true) {
    if (this.#state === "notListening") {
      return;
    }
    this.#state = "notListening";

    logger.pitchDetection.log({ message: "pitchDetector: stopListening", showStack: true });

    this.#audioRecorder.stopListening()
      .then(() => {
        if (analyzeAudio) {
          if (this.#getAudioContext()) {
            this.#audioRecorder.getAudioBuffer()
              .then((buffer) => {
                const shouldPlaybackRecording = localStorageService.getOrCreateBooleanValue(storageKeys.segmentPlayer_pitchDetection_playbackRecording, false);

                if (shouldPlaybackRecording) {
                  playbackBuffer(buffer, this.#getAudioContext()).then(() => {
                    this.#analyzeAudioBuffer(buffer);
                  });
                } else {
                  this.#analyzeAudioBuffer(buffer);
                }
              });
          }
        }
      });
  }

  #analyzeAudioBuffer(buffer: AudioBuffer): void {
    logger.pitchDetection.info({
      message: "pitchDetector: analyzing audio",
      data: [
        {key: "buffer.duration", value: buffer.duration},
        {key: "buffer.length", value: buffer.length},
        {key: "buffer.sampleRate", value: buffer.sampleRate}
      ]
    });

    prepareAudioBufferForTranscription(buffer, this.#getAudioContext())
      .then((preparedBuffer) => {
        logger.pitchDetection.info({
          message: "pitchDetector: audio prepared for transcription",
          data: [
            {key: "preparedBuffer.duration", value: preparedBuffer.duration},
            {key: "preparedBuffer.length", value: preparedBuffer.length},
            {key: "preparedBuffer.sampleRate", value: preparedBuffer.sampleRate}
          ]
        });
        
        transcribeAudioBufferToMidiNotes(preparedBuffer, this.#onTranscribeUpdate)
          .then((notes: NoteEventTime[]) => {
            logger.pitchDetection.info({
              message: "pitchDetector: transcription complete",
              data: [
                {key: "notes.length", value: notes.length}
              ]
            });
            this.#handleNotesDetected(notes);
          });
      });
  }

  // #filterNotesByRangeAndRemoveDuplicates(notes: NoteEventTime[], minPitch: number = lowestNoteOnAPiano, maxPitch: number = highestNoteOnAPiano): NoteEventTime[] {
  //   let result = this.#filterNotesByRange(notes, minPitch, maxPitch);
  //   // result = this.#removeDuplicateNotes(result);
  //   return result;
  // }

  // #filterNotesByRange(notes: NoteEventTime[], minPitch: number = lowestNoteOnAPiano, maxPitch: number= highestNoteOnAPiano): NoteEventTime[] {
  //   const filteredNotes = notes.filter((note: NoteEventTime) => {
  //     let shouldIncludeNote = true;
  //
  //     if (minPitch !== -1) {
  //       shouldIncludeNote = shouldIncludeNote && note.pitchMidi >= minPitch;
  //     }
  //
  //     if (maxPitch !== -1) {
  //       shouldIncludeNote = shouldIncludeNote && note.pitchMidi <= maxPitch;
  //     }
  //
  //     return shouldIncludeNote;
  //   });
  //
  //   return filteredNotes;
  // }

  // #removeDuplicateNotes(notes: NoteEventTime[]): NoteEventTime[] {
  //   // Sort the notes by startTimeSeconds
  //   notes.sort((a, b) => a.startTimeSeconds - b.startTimeSeconds);
  //
  //   const result: NoteEventTime[] = [];
  //   let lastNote: NoteEventTime | null = null;
  //
  //   const startTimeDifferenceThreshold = 0.1;
  //
  //   for (const note of notes) {
  //     // Add note if this is the first instance of the note or the pitchMidi is different or the startTimeSeconds difference is more than 0.3 seconds
  //     if (!lastNote || note.pitchMidi !== lastNote.pitchMidi || Math.abs(note.startTimeSeconds - lastNote.startTimeSeconds) > startTimeDifferenceThreshold) {
  //       result.push(note);
  //       lastNote = note;
  //     }
  //   }
  //
  //   return result;
  // }
}
